import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Market capitalization is a company's total stock-market value, calculated as share price multiplied by shares outstanding. A company trading at $50 with 1 billion shares has a market cap of $50 billion. Market cap is the standard way to size a company and split stocks into large-cap (>$10B), mid-cap ($2–10B), and small-cap (<$2B).";

const FAQS = [
  { q: 'Is market cap the same as company value?', a: 'No. Market cap only reflects equity value. Enterprise value (EV) adds debt and subtracts cash, giving the true price someone would pay to buy the whole business.' },
  { q: 'What is the difference between large-cap, mid-cap, and small-cap?', a: 'Large-cap is over $10 billion (Apple, Coca-Cola). Mid-cap is $2–10 billion. Small-cap is $300 million to $2 billion. Below that is micro-cap or nano-cap, which carry much higher risk.' },
  { q: 'Why does market cap change every day?', a: 'Because share price changes every trading day. Shares outstanding move slowly (buybacks, new issuance), but price moves continuously, so market cap fluctuates with it.' },
  { q: 'Are large-cap stocks safer?', a: 'On average, yes. Large caps are usually more profitable, more diversified, and less volatile. They also tend to grow more slowly than small caps.' },
  { q: 'What is the largest company by market cap right now?', a: 'It rotates among Apple, Microsoft, NVIDIA, Saudi Aramco, and Alphabet. All sit in the $2–4 trillion range. The exact ranking changes month to month with stock prices.' },
];

export default function WhatIsMarketCapPage() {
  return (
    <BlogArticleLayout
      slug="what-is-market-cap"
      title="What Is Market Capitalization? Large-Cap, Mid-Cap & Small-Cap Explained"
      description="Market cap is the simplest way to size a company. Learn the formula, the three size tiers, and why market cap matters for risk and returns."
      keywords="market cap, market capitalization, what is market cap, large cap, mid cap, small cap, market cap formula, mega cap stocks, how to calculate market cap"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-pe-ratio', 'index-funds-vs-stocks', 'trivia-rush']}
      ctaText="Practise sizing companies"
      ctaSubtext="Trivia Rush asks you to compare market caps of real companies, fast. It's the quickest way to build intuition."
      ctaHref="/trivia-rush"
      ctaLabel="Play Trivia Rush"
    >
      <section>
        <h2>What is market capitalization?</h2>
        <p>
          Market capitalization (or "market cap") is the total dollar value of a company's outstanding shares.
          The formula is: <strong>Market Cap = Share Price × Shares Outstanding</strong>.
        </p>
        <p>
          Think of it as the price tag the stock market puts on the whole company. If every share were sold at the current price,
          this is roughly what the equity would be worth. It is the single most common way analysts and journalists talk about company size.
        </p>
      </section>

      <section>
        <h2>How do you calculate market cap?</h2>
        <p>
          Two inputs:
        </p>
        <ul>
          <li><strong>Share price</strong> — the current trading price of one share.</li>
          <li><strong>Shares outstanding</strong> — the total number of shares the company has issued. Listed in every annual report.</li>
        </ul>
        <p>
          Worked example: Coca-Cola trades at $65 with about 4.3 billion shares outstanding. Market cap ≈ $65 × 4.3B = <strong>$280 billion</strong>.
          That puts Coca-Cola comfortably in the large-cap (and mega-cap) tier.
        </p>
      </section>

      <section>
        <h2>What are large-cap, mid-cap, and small-cap stocks?</h2>
        <p>
          Investors split stocks into size buckets because they tend to behave differently:
        </p>
        <ul>
          <li><strong>Mega-cap</strong> — over $200 billion. The handful of giants that dominate indexes (Apple, Microsoft, Saudi Aramco).</li>
          <li><strong>Large-cap</strong> — $10B to $200B. Established, well-known companies. Lower volatility on average.</li>
          <li><strong>Mid-cap</strong> — $2B to $10B. Established but still growing.</li>
          <li><strong>Small-cap</strong> — $300M to $2B. Higher growth potential, higher volatility.</li>
          <li><strong>Micro-cap</strong> — under $300M. Highest risk, often illiquid.</li>
        </ul>
      </section>

      <section>
        <h2>Why does market cap matter?</h2>
        <p>
          Market cap drives three practical things:
        </p>
        <ul>
          <li><strong>Index inclusion.</strong> The S&P 500 is essentially the 500 largest US companies by market cap.</li>
          <li><strong>Risk profile.</strong> Larger companies typically have more diverse revenue and survive recessions better.</li>
          <li><strong>Position sizing.</strong> Many funds size their positions in proportion to market cap.</li>
        </ul>
        <p>
          For a deeper dive into how market cap shapes index investing, see our guide on <Link to="/blog/index-funds-vs-stocks">index funds vs individual stocks</Link>.
        </p>
      </section>

      <section>
        <h2>What is market cap NOT good for?</h2>
        <p>
          Market cap ignores debt and cash. A company with a $50B market cap and $30B of debt is much pricier to actually acquire than a debt-free company at $50B.
          For deal valuation, analysts use <strong>enterprise value</strong> instead: EV = Market Cap + Debt − Cash.
        </p>
        <p>
          Market cap also says nothing about <Link to="/blog/what-is-pe-ratio">whether a stock is cheap or expensive</Link> — only how big the company is.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
